import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { fileUrl } from "@/lib/api";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, CalendarDays, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EASE, MaskLine, scrollToId } from "@/components/motion";
import { txt } from "@/lib/site";

const LINKS = [["pastor", "Our Pastor"], ["team", "Our Team"], ["jadwal", "Jadwal"], ["sermon", "Sermon"], ["komsel", "RC"], ["media", "Media"], ["tentang", "Tentang"]];

export function Navbar({ forceGlass = false }) {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const portal = user ? (user.role === "admin" ? "/admin" : "/portal") : "/login";
  const glass = forceGlass || scrolled || open;

  return (
    <header className="fixed top-4 inset-x-4 md:inset-x-8 z-50">
      <nav data-testid="landing-navbar" className={`max-w-7xl mx-auto flex items-center justify-between rounded-full pl-5 pr-2 py-2 transition-colors duration-500 border ${glass ? "bg-white/90 backdrop-blur-md border-slate-200 shadow-sm text-slate-900" : "bg-transparent border-transparent text-white"}`}>
        <Link to="/" data-testid="landing-logo" className="flex items-center">
          <Logo dark={!glass} className="h-7 md:h-8" />
        </Link>
        <div className="hidden md:flex items-center gap-1 text-sm font-semibold">
          {LINKS.map(([id, label]) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => scrollToId(id)} className={`px-4 py-2 rounded-full transition-colors ${glass ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900" : "text-white/75 hover:text-white"}`}>{label}</button>
          ))}
          <Link to="/persembahan" data-testid="nav-link-persembahan" className={`px-4 py-2 rounded-full transition-colors ${glass ? "text-blue-700 hover:bg-blue-50" : "text-amber-300 hover:text-amber-200"}`}>Persembahan</Link>
          <Link to="/event" data-testid="nav-link-event" className={`px-4 py-2 rounded-full transition-colors ${glass ? "text-blue-700 hover:bg-blue-50" : "text-amber-300 hover:text-amber-200"}`}>Event</Link>
        </div>
        <div className="flex items-center gap-2">
          <Link to={portal} data-testid="nav-portal-button" className="text-sm font-bold bg-blue-600 text-white rounded-full px-5 py-2.5 shadow-md hover:bg-blue-700 active:scale-95 transition">Portal Jemaat</Link>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden w-10 h-10 grid place-items-center rounded-full">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
        </div>
      </nav>
      {open && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="md:hidden mt-2 bg-white/90 backdrop-blur-md border border-slate-200 rounded-3xl p-3 shadow-lg">
          {LINKS.map(([id, label]) => (
            <button key={id} data-testid={`nav-mobile-link-${id}`} onClick={() => { setOpen(false); scrollToId(id); }} className="block w-full text-left px-4 py-3 rounded-2xl font-semibold text-slate-700 hover:bg-slate-100">{label}</button>
          ))}
          <Link to="/persembahan" data-testid="nav-mobile-link-persembahan" onClick={() => setOpen(false)} className="block w-full text-left px-4 py-3 rounded-2xl font-bold text-blue-700 hover:bg-blue-50">Persembahan</Link>
          <Link to="/event" data-testid="nav-mobile-link-event" onClick={() => setOpen(false)} className="block w-full text-left px-4 py-3 rounded-2xl font-bold text-blue-700 hover:bg-blue-50">Event</Link>
        </motion.div>
      )}
    </header>
  );
}

function useTilt() {
  const x = useMotionValue(0.5), y = useMotionValue(0.5);
  const rotY = useSpring(useTransform(x, [0, 1], [-5, 5]), { stiffness: 120, damping: 20 });
  const rotX = useSpring(useTransform(y, [0, 1], [4, -4]), { stiffness: 120, damping: 20 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width);
    y.set((e.clientY - r.top) / r.height);
    e.currentTarget.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    e.currentTarget.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
  };
  const onLeave = () => { x.set(0.5); y.set(0.5); };
  return { rotX, rotY, onMove, onLeave };
}

function HeroBackdrop({ images, y }) {
  const list = images.length ? images : ["https://images.unsplash.com/photo-1579975194233-8a4c97b11f1b?auto=format&fit=crop&w=2200&q=80"];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (list.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % list.length), 7000);
    return () => clearInterval(t);
  }, [list.length]);
  return (
    <motion.div style={{ y }} className="absolute inset-0" data-testid="hero-backdrop">
      {list.map((src, idx) => (
        <motion.img key={src + idx} src={fileUrl(src)} alt="" aria-hidden="true"
          initial={{ opacity: 0, scale: 1.22 }} animate={{ opacity: idx === i ? 0.4 : 0, scale: idx === i ? 1.12 : 1.22 }}
          transition={{ duration: 1.6, ease: EASE }} className="absolute inset-0 w-full h-full object-cover" />
      ))}
    </motion.div>
  );
}

export function Hero({ schedules = [], heroImages = [], badge, site }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const { rotX, rotY, onMove, onLeave } = useTilt();

  return (
    <section ref={ref} onPointerMove={onMove} onPointerLeave={onLeave} data-testid="hero-section" className="relative min-h-[100svh] bg-slate-900 text-white overflow-hidden isolate flex flex-col">
      <HeroBackdrop images={heroImages} y={imgY} />
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-slate-900/90 to-indigo-900/80" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(700px circle at var(--mx,50%) var(--my,35%), rgba(96,165,250,.25), transparent 45%)" }} />
      <div className="absolute inset-0 grain opacity-[0.12] mix-blend-overlay pointer-events-none" />

      <motion.div style={{ rotateX: rotX, rotateY: rotY, opacity: fade, transformPerspective: 1400 }} className="relative z-10 flex-1 max-w-7xl w-full mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-16">
        <motion.span initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8, ease: EASE }} data-testid="hero-badge" className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 text-white shadow-lg rounded-full px-4 py-2 text-sm font-semibold">
          {badge || "✨ Selamatkan 1 Jiwa Lagi!"}
        </motion.span>
        <h1 className="hero-title font-black mt-8" data-testid="hero-title">
          <MaskLine delay={0.45}>{txt(site, "hero_title_1", "GIM Church")}</MaskLine>
          <MaskLine delay={0.6} className="text-gradient pr-3">{txt(site, "hero_title_2", "Makassar.")}</MaskLine>
        </h1>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease: EASE }} className="mt-8 flex flex-col md:flex-row md:items-end gap-8 md:justify-between">
          <p className="max-w-lg text-base md:text-lg text-slate-300 leading-relaxed font-light" data-testid="hero-subtitle">{txt(site, "hero_subtitle", "Grace Immanuel Ministries — rumah bagi setiap generasi untuk memuridkan dalam kebenaran firman, dan menyelamatkan satu jiwa lagi.")}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" data-testid="hero-join-button" className="group inline-flex items-center gap-3 bg-blue-600 text-white font-bold py-3 md:py-4 px-7 rounded-full shadow-md shadow-blue-600/30 hover:bg-blue-500 transform active:scale-95 transition">
              {txt(site, "hero_cta_primary", "Bergabung Sekarang")}<span className="w-7 h-7 rounded-full bg-white/20 grid place-items-center group-hover:translate-x-1 transition-transform"><ArrowRight className="w-4 h-4" /></span>
            </Link>
            <button onClick={() => scrollToId("jadwal")} data-testid="hero-schedule-button" className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/25 text-white font-bold py-3 md:py-4 px-7 rounded-full hover:bg-white/20 transform active:scale-95 transition">
              <CalendarDays className="w-4 h-4" /> {txt(site, "hero_cta_secondary", "Jadwal Ibadah")}
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }} className="relative z-10 border-t border-white/10 bg-slate-900/40 backdrop-blur-md" data-testid="hero-service-strip">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {schedules.slice(0, 4).map((s) => (
            <div key={s.id} className="px-6 md:px-10 py-5">
              <p className="text-[10px] md:text-xs uppercase tracking-widest text-white/50 font-semibold">{s.title}</p>
              <p className="text-xl md:text-2xl font-extrabold mt-1">{s.time} <span className="text-xs font-semibold text-white/50">WITA</span></p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
